import {Checkbox} from "../../basic/checkbox";
import {Badge} from "../../basic/badge";
import {Label} from "../../basic/label";
import {Input} from "../../basic/input";
import {Check} from "lucide-react";
import {useTranslationWithKeys} from "../../../hooks/useTranslationWithKeys";
import type {VwpEntitlement} from "../../../store/generated/api";

interface EntitlementSelectorProps {
    availableEntitlements: string[];
    selectedEntitlements: VwpEntitlement[];
    onEntitlementsChange: (entitlements: VwpEntitlement[]) => void;
    loading?: boolean;
    error?: string;
}

export function EntitlementSelector({
                                        availableEntitlements,
                                        selectedEntitlements,
                                        onEntitlementsChange,
                                        loading = false,
                                        error
                                    }: EntitlementSelectorProps) {
    const {t} = useTranslationWithKeys();

    const selectedEntitlementNames = selectedEntitlements.map(e => e.name);

    const getEntitlementValue = (entitlementName: string): string => {
        const entitlement = selectedEntitlements.find(e => e.name === entitlementName);
        return entitlement?.value || '';
    };

    const getEntitlementLimit = (entitlementName: string, field: 'softLimit' | 'hardLimit'): string => {
        const entitlement = selectedEntitlements.find(e => e.name === entitlementName);
        const limit = entitlement?.[field];
        return limit === undefined || limit === null ? '' : String(limit);
    };

    const parseLimit = (value: string): number | undefined => {
        if (!value.trim()) {
            return undefined;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    };

    const handleToggleEntitlement = (entitlementName: string) => {
        const isSelected = selectedEntitlementNames.includes(entitlementName);

        if (isSelected) {
            // Remove entitlement
            const updated = selectedEntitlements.filter(e => e.name !== entitlementName);
            onEntitlementsChange(updated);
        } else {
            // Add entitlement - create VwpEntitlement object
            const newEntitlement: VwpEntitlement = {
                id: entitlementName,
                name: entitlementName
            };
            onEntitlementsChange([...selectedEntitlements, newEntitlement]);
        }
    };

    const handleValueChange = (entitlementName: string, value: string) => {
        const updated = selectedEntitlements.map(e => {
            if (e.name === entitlementName) {
                return {...e, value};
            }
            return e;
        });
        onEntitlementsChange(updated);
    };

    const handleLimitChange = (entitlementName: string, field: 'softLimit' | 'hardLimit', value: string) => {
        const limit = parseLimit(value);
        const updated = selectedEntitlements.map(e => {
            if (e.name !== entitlementName) {
                return e;
            }

            return {
                ...e,
                [field]: limit
            };
        });
        onEntitlementsChange(updated);
    };

    if (loading) {
        return (
            <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-center py-8">
                    <p>{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                        <p className="text-red-500">{t('common.error')}</p>
                        <p className="text-sm text-muted-foreground mt-1">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 pt-4 border-t">
            <div>
                <p className="text-muted-foreground">
                    {t('admin.products.edit.entitlements.description')}
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {availableEntitlements.map((entitlementName) => {
                    const isSelected = selectedEntitlementNames.includes(entitlementName);
                    const value = getEntitlementValue(entitlementName);
                    const softLimit = getEntitlementLimit(entitlementName, 'softLimit');
                    const hardLimit = getEntitlementLimit(entitlementName, 'hardLimit');

                    return (
                        <div
                            key={entitlementName}
                            className="flex flex-col gap-3 p-4 rounded-lg border hover:bg-muted/30 transition-colors"
                        >
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => handleToggleEntitlement(entitlementName)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div className="flex-1 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <p>{entitlementName}</p>
                                        {isSelected && (
                                            <Check className="h-4 w-4 text-primary"/>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {isSelected && (
                                <div className="space-y-2 ml-6">
                                    <div className="flex items-center gap-2">
                                        <Label htmlFor={`simple-value-${entitlementName}`}>{t('admin.entitlements.valueLabel')}</Label>
                                        <Input
                                            id={`simple-value-${entitlementName}`}
                                            type="text"
                                            value={value}
                                            onChange={(e) => handleValueChange(entitlementName, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Label htmlFor={`soft-limit-${entitlementName}`}>{t('admin.entitlements.softLimitLabel')}</Label>
                                        <Input
                                            id={`soft-limit-${entitlementName}`}
                                            type="number"
                                            value={softLimit}
                                            onChange={(e) => handleLimitChange(entitlementName, 'softLimit', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Label htmlFor={`hard-limit-${entitlementName}`}>{t('admin.entitlements.hardLimitLabel')}</Label>
                                        <Input
                                            id={`hard-limit-${entitlementName}`}
                                            type="number"
                                            value={hardLimit}
                                            onChange={(e) => handleLimitChange(entitlementName, 'hardLimit', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {selectedEntitlements.length > 0 && (
                <div className="p-4 rounded-lg border bg-primary/5">
                    <p className="mb-2">{t('admin.products.edit.entitlements.selected', {count: selectedEntitlements.length})}:</p>
                    <div className="flex flex-wrap gap-2">
                        {selectedEntitlements.map((entitlement, idx) => (
                            <Badge key={idx} variant="secondary">
                                {entitlement.name}
                                {entitlement.value && `: ${entitlement.value}`}
                                {entitlement.softLimit !== undefined && ` (${t('admin.entitlements.softLimitShort')}: ${entitlement.softLimit})`}
                                {entitlement.hardLimit !== undefined && ` (${t('admin.entitlements.hardLimitShort')}: ${entitlement.hardLimit})`}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}